import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Button, Row, Col, Container,
} from 'reactstrap';
import _ from 'lodash';

import FlyoutExpandableDetailsItem from '../FlyoutExpandableDetailsItem';
import RelativeDatestamp from '../RelativeDatestamp';
import AddNewButton from '../AddNewButton';
import Alert from '../Alert';
import {
  validate,
  format,
  TextField,
  attachRefToFields,
  FlyoutInputGroup,
  ReactSelectField,
} from '../Form';


class SensorDetail extends Component {
  getInstalledModulesComponents(props) {
    const { sensor } = props;
    const { installedModules } = sensor || [];

    const moduleList = _.map(installedModules, (module, i) => {
      // Find the module specification so we can show more information about it
      const moduleSpec = module.spec;
      const version = module.tag || '';
      // The spec has a cleaner name without the version crammed into it
      const moduleName = moduleSpec ? moduleSpec.name : module.name;
      const description = moduleSpec ? moduleSpec.notes : '';
      const createdDate = module.created || '';
      const moreInfoPorts = _.map(module.config.ports, port => ({
        description: port.description || 'No information available',
        containerPort: port.containerPort || '<random>',
        protocol: port.protocol || 'unknown',
      }));
      const moreInfoVolumes = _.map(module.config.volumes, volume => ({
        files: volume.files || [],
        targetPath: volume.targetPath || 'unknown',
      }));
      const moreInfoEnv = _.map(module.config.env, (envValue, envName) => ({
        name: envName || 'UNKNOWN',
        value: envValue || '',
      }));

      // @TODO: Consider making this left/right detail construct a Component.
      // I was waiting for the next time it was used before I did,
      return (
        <li key={i}>
          <FlyoutExpandableDetailsItem
            classes='installed-sensor-module'
            text={`${moduleName} ${version}`}
          >
            <Container>
              <Row>
                <Col xs='3'>Notes:</Col>
                <Col xs='9'>{description}</Col>
              </Row>
              { createdDate && // For now, the API doesn't return this, but it will soon
                <Row>
                  <Col xs='3'>Created:</Col>
                  <Col xs='9'><RelativeDatestamp date={createdDate} /></Col>
                </Row>
              }
              {
                _.map(moreInfoPorts, (port, j) =>
                  (
                    <Row key={j}>
                      <Col xs='3'>{j === 0 ? 'Ports:' : ''}</Col>
                      <Col xs='9'>
                        <span>{`${port.containerPort} ${port.protocol}`}</span>
                        <span>{port.description}</span>
                      </Col>
                    </Row>
                  ))
              }
              {
                _.map(moreInfoEnv, (env, j) =>
                  (
                    <Row key={j}>
                      <Col xs='3'>{j === 0 ? 'Environment:' : ''}</Col>
                      <Col xs='9'>
                        <div className='sensor-module-env'>
                          <span className='sensor-module-env-name'>
                            {env.name}
                          </span>
                          <span className='sensor-module-env-value'>
                            {this.truncateObfuscatedValue(env.value)}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  ))
              }
              {
                _.map(moreInfoVolumes, (volume, j) =>
                  (
                    <Row key={j}>
                      <Col xs='3'>{j === 0 ? 'Volumes:' : ''}</Col>
                      <Col xs='9'>
                        <span className='sensor-module-volume-name'>{volume.targetPath}</span>
                        {
                          _.map(volume.files, (file, k) => (
                            <div key={k} className='sensor-module-volume-file'>
                              <span className='sensor-module-volume-file-name'>
                                {file.name}
                              </span>
                              <span className='sensor-module-volume-file-contents'>
                                {this.truncateObfuscatedValue(file.contents)}
                              </span>
                            </div>
                          ))
                        }
                      </Col>
                    </Row>
                  ))
              }
              <Row>
                <Col xs='3' />
                <Col xs='9'>
                  <Button
                    className='sensor-module-edit-button'
                    size='sm'
                    onClick={() => this.handleEditModule(module)}
                  >
                    Edit This Module
                  </Button>
                </Col>
              </Row>
            </Container>
          </FlyoutExpandableDetailsItem>
        </li>);
    });

    return (
      <div>
        <ul className='installed-sensor-module-list'>
          {moduleList}
        </ul>
      </div>);
  }

  // *************************************MTIz
  // There are just a few too many **'s... we get it
  truncateObfuscatedValue(contents) {
    return _.replace(contents, /\*{8,}/, '****');
  }

  handleEditModule(module) {
    const { handleEditModule } = this.props;
    handleEditModule(module);
  }

  prepForSubmit(formValues, props) {
    const {
      id,
      name,
      location_id,
    } = formValues;

    const { orgId } = props;

    const prepped = {
      id,
      name,
      org_id: orgId,
      location_id: location_id ? location_id.value : '',
    };

    return prepped;
  }

  reportError(error) {
    console.error(err); // eslint-disable-line
    this.props.handleError(error);
  }

  handleAddNewModule() {
    const { handleAddNewModule } = this.props;
    handleAddNewModule();
  }

  render() {
    const {
      sensor = {},
      locations = [],
      mode,
    } = this.props;
    const { installedModules } = sensor;
    const hasInstalledModules = !_.isNil(installedModules) && installedModules.length > 0;
    const showInstalledModules = mode === 'edit';

    // If we are in edit mode but don't have a sensor, there's nothing to do.
    // This is most useful when we error out fetching a sensor. We don't want
    // to sit there and look like we're in 'create' mode.
    if (mode === 'edit' && (_.isNil(sensor) || _.isEmpty(sensor))) {
      return null;
    }

    const saveButtonText = mode === 'edit' ? 'Save Changes' : 'Create Sensor';

    const locationOptions = _.map(locations, a => ({
      label: a.name,
      value: a.id,
    }));
    const location = _.find(locationOptions, l => l.value === sensor.location_id);

    const initialValues = {
      name: '', // required
      ..._.omitBy(sensor, _.isNil),
    };

    // Once you set a location, you can't change it
    const allowEditingLocation = (mode === 'create');
    if (allowEditingLocation) {
      // Needs to be in initialValues in this case
      initialValues.location_id = location ? location : initialValues.location_id;
    }

    return (
      <React.Fragment>
        <Formik
          initialValues={initialValues}
          ref={(c) => { this.form = c; }}
          enableReinitialize
          validate={values => validate(this, values)}
          onSubmit={async (values, { setSubmitting, setErrors }) => { // eslint-disable-line
            const { handleApply } = this.props;

            let newValues = values;
            try {
              newValues = await format(this, values);
            } catch (err) {
              this.reportError(err);
              return;
            }

            setSubmitting(true);
            let resp;
            try {
              const preppedValues = this.prepForSubmit(newValues, this.props);
              resp = await handleApply(preppedValues);
            } catch (err) {
              this.reportError(err);
              return;
            }
            setSubmitting(false);
            return resp;
          }}
          render={(formProps) => { // eslint-disable-line
            const page = this;

            return (
              <React.Fragment>
                <h1 className='title'>{sensor.name}</h1>
                <div className='subtitle'>{location && location.label}</div>
                <form onSubmit={formProps.handleSubmit}>
                  <div className='form-group'>
                    <FlyoutInputGroup
                      name='name'
                      label='Name'
                      required
                    >
                      <TextField
                        type='text'
                        name='name'
                        disableLabel
                        classes='flyout-text-field'
                        validation={{
                          length: { min: 2, max: 100 },
                          alnumString: true,
                        }}
                        formatting={{ asString: {} }}
                        placeholder='example-sensor-name'
                        required
                        ref={attachRefToFields.bind(this, page)}
                        {...formProps}
                      />
                    </FlyoutInputGroup>
                    { allowEditingLocation &&
                      <FlyoutInputGroup name='location_id' label='Org Location' required>
                        <ReactSelectField
                          name='location_id'
                          required
                          options={locationOptions}
                          ref={attachRefToFields.bind(this, page)}
                          {...formProps}
                        />
                      </FlyoutInputGroup>
                    }
                  </div>
                </form>
                <div className='actions'>
                  <Button color='danger' onClick={this.props.handleCancel}>Cancel</Button>
                  <Button color='primary' onClick={() => formProps.handleSubmit()}>{saveButtonText}</Button>
                </div>
              </React.Fragment>
            );
          }}
        />
        { showInstalledModules &&
          <section>
            <h5>Installed Modules:</h5>
            { hasInstalledModules
              ?
                <React.Fragment>
                  { this.getInstalledModulesComponents(this.props) }
                  <section>
                    <AddNewButton handleAction={() => this.handleAddNewModule()} text='Add a module' />
                  </section>
                </React.Fragment>
              :
                <Alert
                  color='info'
                  actionName='Add a module'
                  handleAction={() => this.handleAddNewModule()}
                  message='There are no modules installed.'
                  subtitle='Next Steps:'
                  details='You add modules to sensors to add new functionality. Your Sensor should at least have a Logger module installed.'
                />
            }
          </section>
        }
      </React.Fragment>
    );
  }
}

SensorDetail.propTypes = {
  mode: PropTypes.string,
  orgId: PropTypes.string,
  sensor: PropTypes.shape({}),
  locations: PropTypes.instanceOf(Array),
  handleApply: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  handleAddNewModule: PropTypes.func.isRequired,
  handleEditModule: PropTypes.func.isRequired,
};

SensorDetail.defaultProps = {
  mode: 'edit',
  orgId: '',
  sensor: {},
  locations: [],
};

export default SensorDetail;



// WEBPACK FOOTER //
// ./src/views/Components/Detail/SensorDetail.js